/**
 * Cotización en hoja carta (8.5x11), con el mismo estilo de casa que la
 * factura de motor (impresionfacturamotor.js).
 *
 * Es un documento informativo: no compromete la venta ni reserva el chasis,
 * y así lo dice la nota al pie. Muestra siempre el precio de contado y, si la
 * cotización incluye plan de crédito, el desglose inicial / financiamiento /
 * valor de la cuota (el interés no se imprime, misma política que la factura).
 */
import { imprimirHtml } from "lib/impresionhtml";

const NAVY = "#18586b";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const esc = (s) =>
  String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));

// Logo del membrete: solo data URLs de imagen (lo que guarda configuración);
// cualquier otra cosa se ignora para no inyectar HTML en la impresión.
const logoImg = (comp) =>
  comp && typeof comp.logo === "string" && comp.logo.startsWith("data:image/")
    ? `<img class="logo" src="${comp.logo}" alt="" />`
    : "";

const montoHtml = (etiqueta, valor, destacado = false) =>
  `<tr class="${destacado ? "destacado" : ""}">
     <td>${esc(etiqueta)}</td>
     <td class="num">RD$ ${fmtMoney(valor)}</td>
   </tr>`;

const datoHtml = (etiqueta, valor) =>
  valor ? `<div><span>${esc(etiqueta)}</span><b>${esc(valor)}</b></div>` : "";

/**
 * @param {Object} det            Respuesta de /api/cotizacion_detalle o _crear.
 * @param {Object} det.cotizacion Fila de la tabla cotizaciones ya resuelta.
 * @param {Object} det.compania   Membrete {nombre, rnc, direccion, telefono, logo}.
 */
export function imprimirCotizacion(det) {
  const q = det?.cotizacion || {};
  const c = det?.compania || {};
  const conCredito = (q.concredito || "") === "S";
  const cliente = [q.nombres, q.apellidos].filter(Boolean).join(" ");
  const vehiculo = [q.marca, q.modelo, q.colore].filter(Boolean).join(" · ");
  const plazo = Number(q.plazo) || 0;
  const periodo = q.formadepago || "";

  const montos = conCredito
    ? [
        montoHtml("Precio de contado", q.precio),
        montoHtml("Inicial", q.inicial),
        montoHtml("Financiamiento", q.financia),
        montoHtml(
          plazo ? `Valor de la cuota (${plazo} ${periodo})` : "Valor de la cuota",
          q.valorcuota
        ),
        montoHtml("Total a pagar", (Number(q.deudatotal) || 0) + (Number(q.inicial) || 0), true),
      ].join("")
    : montoHtml("Precio de contado", q.precio, true);

  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>Cotización</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 60px; max-width: 170px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .doc .vence { color: ${NAVY}; font-weight: bold; }
      .bloque { margin: 14px 0; border: 1px solid #d7dee5; border-radius: 6px; padding: 10px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      .rotulo { margin: 16px 0 4px; font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      table.montos { width: 55%; border-collapse: collapse; }
      table.montos td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; }
      td.num { text-align: right; }
      tr.destacado td { font-weight: bold; border-top: 2px solid ${NAVY}; border-bottom: none;
                        background: #f0f5f8; color: ${NAVY}; font-size: 14px; }
      .nota { margin-top: 24px; padding: 10px 14px; border: 1px solid #d7dee5; border-radius: 6px;
              font-size: 10.5px; color: #4a5866; }
      .firmas { display: flex; justify-content: space-around; margin-top: 70px; }
      .firmas div { width: 210px; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px;
                    font-size: 11px; color: #4a5866; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logoImg(c)}
          <div>
            <h1>${esc(c.nombre)}</h1>
            ${c.direccion ? `<p>${esc(c.direccion)}</p>` : ""}
            <p>${[c.rnc ? `RNC: ${esc(c.rnc)}` : "", c.telefono ? `Tel: ${esc(c.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Cotización</div>
          <p>${conCredito ? "Contado / Crédito" : "Contado"}</p>
          <p># Cotización: <b>${esc(q.id)}</b></p>
          <p>Fecha: <b>${esc(q.fecha)}</b></p>
          ${q.fechavence ? `<p class="vence">Válida hasta: ${esc(q.fechavence)}</p>` : ""}
        </div>
      </div>
      <div class="bloque">
        ${datoHtml("Cliente", cliente)}
        ${datoHtml("Cédula", q.cedula)}
        ${datoHtml("Teléfono", q.telefono)}
        ${datoHtml("Vendedor", q.vendedor)}
      </div>
      <div class="rotulo">Vehículo</div>
      <div class="bloque">
        ${datoHtml("Chasis", q.chasis)}
        ${datoHtml("Marca / Modelo / Color", vehiculo)}
        ${datoHtml("Condición", q.condicion)}
      </div>
      ${
        q.comentario
          ? `<div class="rotulo">Comentario</div><div class="bloque"><b>${esc(
              q.comentario
            )}</b></div>`
          : ""
      }
      <div class="rotulo">${conCredito ? "Plan de crédito" : "Detalle"}</div>
      <table class="montos">${montos}</table>
      <div class="nota">
        Esta cotización es solo informativa: no constituye compromiso de venta ni reserva del
        vehículo. Precios sujetos a cambio sin previo aviso.${
          q.fechavence ? ` Válida hasta el ${esc(q.fechavence)}.` : ""
        }
      </div>
      <div class="firmas">
        <div>Preparado por</div>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

export default imprimirCotizacion;
